import {
  ContractPriceTariffInterval,
  DistanceTariffInterval,
} from "../../models/ServicePriceBaseDto";

import Input, { Input$LiveChangeEvent } from "sap/m/Input";
import JSONModel from "sap/ui/model/json/JSONModel";
import BaseController from "./BaseController";
import Column from "sap/ui/table/Column";
import Table from "sap/ui/table/Table";
import Label from "sap/m/Label";
import HBox from "sap/m/HBox";
import VBox from "sap/m/VBox";

// This class expects the child controller to manage a JSONModel named "servicePrice"
export default class TariffPriceTableController extends BaseController {
  protected resetViewOptions() {
    this.setModel(new JSONModel({ canTariffsBeEdited: true }), "viewOptions");
  }

  protected setCanTariffsBeEdited(value: boolean) {
    this.getModel("viewOptions").setProperty("/canTariffsBeEdited", value);
  }

  public addTariffTableRow() {
    const tableRowData =
      (this.getModel("servicePrice").getProperty(
        "/contractPriceTariffIntervals"
      ) as ContractPriceTariffInterval[]) || [];

    let newRowData: ContractPriceTariffInterval;
    if (tableRowData.length === 0) {
      newRowData = {
        lowerBound: 0,
        upperBound: undefined,
        prices: [0],
      };
    } else {
      const lastRowData = tableRowData[tableRowData.length - 1];
      newRowData = {
        lowerBound: lastRowData.upperBound ?? 0,
        upperBound: undefined,
        prices: [...lastRowData.prices],
      };
    }

    tableRowData[tableRowData.length - 1].upperBound =
      tableRowData[tableRowData.length - 1].lowerBound;
    tableRowData.push(newRowData);

    this.getModel("servicePrice").setProperty(
      "/contractPriceTariffIntervals",
      tableRowData
    );
  }

  public addTariffTableColumn() {
    const tableColumnData = this.getModel("servicePrice").getProperty(
      "/distanceTariffIntervals"
    ) as DistanceTariffInterval[];
    const lastColumnData = tableColumnData[tableColumnData.length - 1];
    tableColumnData.push({
      lowerBound: lastColumnData.upperBound ?? 0,
      upperBound: undefined,
    });

    this.getModel("servicePrice").setProperty(
      "/distanceTariffIntervals",
      tableColumnData
    );

    const tableRowData = this.getModel("servicePrice").getProperty(
      "/contractPriceTariffIntervals"
    ) as ContractPriceTariffInterval[];
    tableRowData.forEach((row) => {
      row.prices.push(0);
    });
    this.getModel("servicePrice").setProperty(
      "/contractPriceTariffIntervals",
      tableRowData
    );

    this.addTariffTableColumnInternal(tableColumnData.length - 1);
  }

  public removeLastColumn() {
    const tableColumnData = this.getModel("servicePrice").getProperty(
      "/distanceTariffIntervals"
    ) as DistanceTariffInterval[];
    if (tableColumnData.length <= 1) return;

    tableColumnData.splice(tableColumnData.length - 1, 1);
    const lastColumnData = tableColumnData[tableColumnData.length - 1];
    lastColumnData.upperBound = undefined;

    const priceIntervals = this.getModel("servicePrice").getProperty(
      "/contractPriceTariffIntervals"
    ) as ContractPriceTariffInterval[];
    priceIntervals.forEach((pI) => {
      pI.prices.pop();
    });
    this.getModel("servicePrice").setProperty(
      "/contractPriceTariffIntervals",
      priceIntervals
    );

    this.getModel("servicePrice").setProperty(
      "/distanceTariffIntervals",
      tableColumnData
    );

    // const tariffTable = this.byId("tariff-interval-table") as Table;
    // const tableCols = tariffTable.getColumns();
    // tariffTable.removeColumn(tableCols[tableCols.length - 1]);

    this.refreshTariffIntervalTable();
  }

  public removeLastRow() {
    const priceIntervals = this.getModel("servicePrice").getProperty(
      "/contractPriceTariffIntervals"
    ) as ContractPriceTariffInterval[];
    if (priceIntervals.length <= 1) return;

    priceIntervals.splice(priceIntervals.length - 1, 1);
    const lastRowData = priceIntervals[priceIntervals.length - 1];
    lastRowData.upperBound = undefined;

    this.getModel("servicePrice").setProperty(
      "/contractPriceTariffIntervals",
      priceIntervals
    );

    this.refreshTariffIntervalTable();
  }

  public refreshTariffIntervalTable(readOnly = false) {
    const distanceIntervalCount = this.getModel("servicePrice").getProperty(
      "/distanceTariffIntervals/length"
    ) as number;

    const tariffTable = this.byId("tariff-interval-table") as Table;
    tariffTable.destroyColumns();
    tariffTable.addColumn(
      new Column({
        width: "17em",
        template: new HBox({
          fitContainer: true,
          displayInline: true,
          alignItems: "Center",
          justifyContent: "Center",
          items: readOnly
            ? [
              new Label({
                text: "{= ${servicePrice>lowerBound} ? ${servicePrice>lowerBound} : '0'}",
                design: "Bold",
              }),
              new Label({
                text: `\u2264 {i18n>mpcEur} <`,
              }).addStyleClass("sapUiTinyMarginBeginEnd"),
              new Label({
                text: {
                  path: "servicePrice>upperBound",
                  formatter: function (value: string | number) {
                    return value === undefined || value === null
                      ? "∞"
                      : value;
                  },
                },
                design: "Bold",
              }),
            ]
            : [
              new Input({
                value: {
                  path: "servicePrice>lowerBound",
                  formatter: function (value: string | number) {
                    return value === undefined || value === null ? 0 : value;
                  },
                },
                type: "Number",
                liveChange: (event: Input$LiveChangeEvent) => {
                  const text = event.getParameter("value");
                  if (text === undefined) return;
                  const value = parseFloat(text);
                  if (isNaN(value) || value.toString() !== text) return;

                  const path =
                    (event
                      .getSource()
                      ?.getBinding("value")
                      ?.getContext()
                      ?.getPath() as string) || "some/value/-1";

                  const lastSlashIndex = path.lastIndexOf("/");
                  if (lastSlashIndex === -1) return;

                  const lastPart = path.slice(lastSlashIndex + 1);
                  const index = parseInt(lastPart, 10);
                  if (isNaN(index)) return;
                  const pathPrefix = path.slice(0, lastSlashIndex);
                  const servicePriceModel = this.getModel("servicePrice");
                  const contractPriceTariffIntervals =
                    servicePriceModel.getProperty(
                      "/contractPriceTariffIntervals"
                    ) as ContractPriceTariffInterval[];

                  const curr = contractPriceTariffIntervals[index];
                  curr.lowerBound = value;
                  servicePriceModel.setProperty(
                    `${pathPrefix}/${index}`,
                    curr
                  );

                  if (index > 0) {
                    const prop = contractPriceTariffIntervals[index - 1];
                    prop.upperBound = value;
                    servicePriceModel.setProperty(
                      `${pathPrefix}/${index - 1}`,
                      prop
                    );
                  }
                },
                textAlign: "End",
                editable: false,
                enabled: "{viewOptions>/canTariffsBeEdited}",
              }),
              new Label({
                text: `\u2264 {i18n>mpcEur} <`,
              }).addStyleClass("sapUiTinyMarginBeginEnd"),
              new Input({
                value: {
                  path: "servicePrice>upperBound",
                  formatter: function (value: string | number) {
                    return value === undefined || value === null
                      ? "∞"
                      : value;
                  },
                },
                type: {
                  path: "servicePrice>upperBound",
                  formatter: function (value: string | number) {
                    return value === undefined || value === null
                      ? "Text"
                      : "Number";
                  },
                },
                liveChange: (event: Input$LiveChangeEvent) => {
                  const text = event.getParameter("value");
                  if (text === undefined) return;
                  const value = parseFloat(text);
                  if (isNaN(value) || value.toString() !== text) return;
                  const path =
                    (event
                      .getSource()
                      ?.getBinding("value")
                      ?.getContext()
                      ?.getPath() as string) || "some/value/-1";

                  const lastSlashIndex = path.lastIndexOf("/");
                  if (lastSlashIndex === -1) return;

                  const lastPart = path.slice(lastSlashIndex + 1);
                  const index = parseInt(lastPart, 10);
                  if (isNaN(index)) return;
                  const pathPrefix = path.slice(0, lastSlashIndex);
                  const servicePriceModel = this.getModel("servicePrice");
                  const contractPriceTariffIntervals =
                    servicePriceModel.getProperty(
                      "/contractPriceTariffIntervals"
                    ) as ContractPriceTariffInterval[];

                  const curr = contractPriceTariffIntervals[index];
                  curr.upperBound = value;
                  servicePriceModel.setProperty(
                    `${pathPrefix}/${index}`,
                    curr
                  );

                  if (index < contractPriceTariffIntervals.length - 1) {
                    const prop = contractPriceTariffIntervals[index + 1];
                    prop.lowerBound = value;
                    servicePriceModel.setProperty(
                      `${pathPrefix}/${index + 1}`,
                      prop
                    );
                  }
                },
                editable: {
                  path: "servicePrice>upperBound",
                  formatter: function (value: string | number) {
                    return value === undefined || value === null
                      ? false
                      : true;
                  },
                },
                enabled: "{viewOptions>/canTariffsBeEdited}",
              }),
            ],
        }),
      })
    );

    for (let ind = 0; ind < distanceIntervalCount; ind++) {
      this.addTariffTableColumnInternal(ind, readOnly);
    }

    tariffTable.bindRows({
      path: "servicePrice>/contractPriceTariffIntervals",
    });
  }

  private updateDistanceTariffIntervalLowerAtIndex(
    columnIndex: number,
    value: number
  ) {
    const servicePriceModel = this.getModel("servicePrice") as JSONModel;
    const distanceTariffIntervals =
      (servicePriceModel?.getProperty(
        "/distanceTariffIntervals"
      ) as DistanceTariffInterval[]) || [];

    distanceTariffIntervals[columnIndex].lowerBound = value;
    if (columnIndex > 0) {
      distanceTariffIntervals[columnIndex - 1].upperBound = value;
    }

    servicePriceModel.setProperty(
      "/distanceTariffIntervals",
      distanceTariffIntervals
    );
  }
  private updateDistanceTariffIntervalUpperAtIndex(
    columnIndex: number,
    value?: number
  ) {
    const servicePriceModel = this.getModel("servicePrice") as JSONModel;
    const distanceTariffIntervals =
      (servicePriceModel?.getProperty(
        "/distanceTariffIntervals"
      ) as DistanceTariffInterval[]) || [];

    distanceTariffIntervals[columnIndex].upperBound = value;
    if (columnIndex < distanceTariffIntervals.length - 1) {
      distanceTariffIntervals[columnIndex + 1].lowerBound = value as
        | string
        | number;
    }

    servicePriceModel.setProperty(
      "/distanceTariffIntervals",
      distanceTariffIntervals
    );
  }

  private addTariffTableColumnInternal(columnIndex: number, readonly = false) {
    const tariffTable = this.byId("tariff-interval-table") as Table;
    const servicePriceModel = this.getModel("servicePrice") as JSONModel;

    tariffTable.addColumn(
      new Column({
        width: "15em",
        label: new HBox({
          width: "100%",
          alignItems: "Center",
          justifyContent: "Center",
          items: readonly
            ? [
              new Label({
                text: {
                  model: "servicePrice",
                  path: `/distanceTariffIntervals/${columnIndex}/lowerBound`,
                  formatter: function (value: number) {
                    return value === undefined || value === null ? 0 : value;
                  },
                },
                design: "Bold",
              }),
              new Label({
                text: `\u2264 {i18n>kmUnit} <`,
              }).addStyleClass("sapUiTinyMarginBeginEnd"),
              new Label({
                text: {
                  model: "servicePrice",
                  path: `/distanceTariffIntervals/${columnIndex}/upperBound`,
                  formatter: function (value: string | number) {
                    return value === undefined || value === null
                      ? "∞"
                      : value;
                  },
                },
                design: "Bold",
              }),
            ]
            : [
              new Input({
                value: {
                  model: "servicePrice",
                  path: `/distanceTariffIntervals/${columnIndex}/lowerBound`,
                  formatter: function (value: number) {
                    return value === undefined || value === null ? 0 : value;
                  },
                },
                type: "Number",
                textAlign: "End",
                editable: false,
                enabled: "{viewOptions>/canTariffsBeEdited}",
                liveChange: (event: Input$LiveChangeEvent) => {
                  const text = event.getParameter("value");
                  if (text !== undefined) {
                    const value = parseFloat(text);
                    if (isNaN(value) || value.toString() !== text) return;
                    this.updateDistanceTariffIntervalLowerAtIndex(
                      columnIndex,
                      value
                    );
                  }
                },
              }),
              new Label({
                text: `\u2264 {i18n>kmUnit} <`,
              }).addStyleClass("sapUiTinyMarginBeginEnd"),
              new Input({
                value: {
                  model: "servicePrice",
                  path: `/distanceTariffIntervals/${columnIndex}/upperBound`,
                  formatter: function (value: string | number) {
                    return value === undefined || value === null
                      ? "∞"
                      : value;
                  },
                },
                type: {
                  model: "servicePrice",
                  path: `/distanceTariffIntervals/${columnIndex}/upperBound`,
                  formatter: function (value: string | number) {
                    return value === undefined || value === null
                      ? "Text"
                      : "Number";
                  },
                },
                editable: {
                  model: "servicePrice",
                  path: `/distanceTariffIntervals/${columnIndex}/upperBound`,
                  formatter: function (value: string | number) {
                    return value === undefined || value === null
                      ? false
                      : true;
                  },
                },
                enabled: "{viewOptions>/canTariffsBeEdited}",
                liveChange: (event: Input$LiveChangeEvent) => {
                  const text = event.getParameter("value");
                  let value = undefined;
                  if (text !== undefined) {
                    value = parseFloat(text);
                    if (isNaN(value) || value.toString() !== text) return;
                  }
                  this.updateDistanceTariffIntervalUpperAtIndex(
                    columnIndex,
                    value
                  );
                },
              }),
            ],
        }),
        template: new HBox({
          fitContainer: true,
          displayInline: true,
          alignItems: "Center",
          justifyContent: "Center",
          items: [
            new VBox({
              width: "100%",
              alignItems: "Center",
              justifyContent: "Center",
              items: readonly
                ? new Label({
                  text: {
                    path: "servicePrice>prices/" + columnIndex,
                    formatter: this.formatPrice,
                  },
                  design: "Bold",
                })
                : new Input({
                  textAlign: "End",
                  type: "Number",
                  value: "{servicePrice>prices/" + columnIndex + "}",
                  enabled: "{viewOptions>/canTariffsBeEdited}",
                }),
            }),
          ],
        }),
      })
    );

    const distanceTariffIntervals =
      (servicePriceModel?.getProperty(
        "/distanceTariffIntervals"
      ) as DistanceTariffInterval[]) || [];

    if (
      columnIndex === distanceTariffIntervals.length - 1 &&
      distanceTariffIntervals[columnIndex - 1] &&
      (distanceTariffIntervals[columnIndex - 1].upperBound === undefined ||
        distanceTariffIntervals[columnIndex - 1].upperBound === null)
    ) {
      distanceTariffIntervals[columnIndex - 1].upperBound =
        distanceTariffIntervals[columnIndex - 1].lowerBound;

      distanceTariffIntervals[columnIndex].lowerBound =
        distanceTariffIntervals[columnIndex - 1].lowerBound;

      servicePriceModel.setProperty(
        "/distanceTariffIntervals",
        distanceTariffIntervals
      );
    }
  }
}
